import { useEffect, useState } from 'react'

// material-ui
import { Button, Chip, MenuItem, Box, Typography } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { PlusCircleOutlined, EyeOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined, } from '@ant-design/icons'

// project import
import { useAuth } from 'services/hook/Auth'
import { useApiData } from 'services/hook/useApiData'
import { useEventBusContext } from 'services/provider/EventBusProvider'
import { PageHeader } from 'components/PageHeader'
import { getPermissionKeys } from 'utils/util'
import { sanitizeFilters } from 'utils/helpers'
import { stockUnitList, stockUnitToggleStatus } from 'models/stockUnit'
import { EVENT_STOCKUNIT_ADD_INIT, EVENT_STOCKUNIT_UPDATE_INIT, EVENT_STOCKUNIT_UPDATE_SUCCESS, EVENT_STOCKUNIT_DETAIL_INIT, EVENT_STOCKUNIT_DELETE_INIT, } from 'models/events/stockUnit.event'
import DataGridList from '../../components/data-grid/DataGridList'
import StockUnitAddDialog from './StockUnit.Create'
import StockUnitEditDialog from './StockUnit.Update'
import StockUnitDetailDialog from './StockUnit.Detail'
import CellRenderer from 'components/list/CellRenderer';

/**
 * List stock units page — permission-gated, same shell as ProductList.
 */
const StockUnitList = () => {
    const permissionsKeys = getPermissionKeys('stock_unit')
    return (
        <>
            <PageHeader title="Stock Units" breadcrumbs={[{ path: '#', label: 'Stock Units' }]} />
            <StockUnitListTable permissionsKeys={permissionsKeys} />
            {permissionsKeys.can_create ? <StockUnitAddDialog /> : null}
            {permissionsKeys.can_update ? <StockUnitEditDialog /> : null}
            {permissionsKeys.can_view && <StockUnitDetailDialog />}
        </>
    )
}

const StockUnitListTable = ({ permissionsKeys }) => {
    const eventbus = useEventBusContext()
    const is_mobile = useMediaQuery('(max-width:991px)')
    const initdata = { data: [], total: 0 }
    const initparams = { page: 0, limit: 10, sort: '', orderby: '', filters: {} }

    const colheadsx = {
        '& .MuiDataGrid-cellCheckbox': { alignItems: 'center' },
        '& .MuiDataGrid-topContainer': { opacity: 0, visibility: 'hidden' },
        '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #c5c5c5a1 !important',
            fontSize: '0.875rem',
            paddingTop: '10px !important',
            paddingBottom: '10px !important',
            outline: 'none !important',
            display: 'flex !important',
            alignItems: 'center !important',
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        }
    }

    const desktopGridSx = {
        '& .MuiDataGrid-cell': {
            fontSize: '0.875rem',
            paddingTop: '10px !important',
            paddingBottom: '10px !important',
            lineHeight: 1.5,
            outline: 'none !important',
            display: 'flex !important',
            alignItems: 'center !important',
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        },
        '& .MuiDataGrid-row': {
            minHeight: '48px !important',
            borderBottom: '1px solid #e0e0e0 !important',
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        },
        '& .MuiDataGrid-cellContent': {
            fontSize: '0.875rem'
        },
        '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#f5f5f5',
            fontSize: '1rem',
            fontWeight: 600,
            outline: 'none !important',
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        },
        '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '1rem',
            fontWeight: 600,
            outline: 'none !important',
            '&:focus': {
                outline: 'none !important'
            }
        },
        '& .MuiDataGrid-columnHeaderTitleContainer': {
            outline: 'none !important',
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        }
    }

    const [data, params, loading, loadData] = useApiData(initdata, initparams, (params, setData) => {
        const sanitizedFilters = sanitizeFilters(params.filters || {})
        const apiParams = {
            ...params,
            ...sanitizedFilters,
            page: (params.page || 0) + 1,
        }
        stockUnitList(apiParams)
            .then((res) => setData(res))
            .catch((err) => console.error('Failed to load stock units:', err))
    }, false)

    useEffect(() => {
        return eventbus.on(EVENT_STOCKUNIT_UPDATE_SUCCESS, () => {
            loadData(params)
        })
    }, [eventbus, params, loadData])

    return (
        <DataGridList
            rows={data.data}
            columns={!is_mobile ? columns(eventbus) : mobile_cols(eventbus)}
            getRowHeight={() => 'auto'}
            actions={<ListActions permissionsKeys={permissionsKeys} eventbus={eventbus} />}
            loading={loading}
            filterkey="stockunitlist"
            sx={!is_mobile ? desktopGridSx : colheadsx}
            columnHeaderHeight={!is_mobile ? 56 : 0}
            search={(query) => loadData({ ...params, query })}
            rowCount={data.total}
            paginationMode="server"
            paginationModel={{ page: params.page, pageSize: params.limit }}
            onPaginationModelChange={(pagination) =>
                loadData({ ...params, page: pagination.page, limit: pagination.pageSize })
            }
            sortingMode="server"
            onSortModelChange={(sortModel) => {
                if (sortModel.length > 0) {
                    loadData({ ...params, orderby: sortModel[0].field, sort: sortModel[0].sort })
                } else {
                    loadData({ ...params, orderby: '', sort: '' })
                }
            }}
            onInit={(pagination, sortModel, search, filters) => {
                let inparam = { query: search, filters: filters }
                if (pagination !== undefined) {
                    inparam = { ...inparam, page: pagination.page, limit: pagination.pageSize }
                }
                if (sortModel !== undefined && sortModel.length > 0) {
                    inparam = { ...inparam, orderby: sortModel[0].field, sort: sortModel[0].sort }
                }
                loadData({ ...params, ...inparam })
            }}
        />
    )
}

const ListActions = ({ eventbus }) => {
    const { checkPermission } = useAuth()
    return checkPermission('stock_unit.create') ? (
        <Button
            variant="contained"
            onClick={() => eventbus.fire(EVENT_STOCKUNIT_ADD_INIT)}
            endIcon={<PlusCircleOutlined />}
        >
            Add Stock Unit
        </Button>
    ) : null
}

// Mobile action menu (used inside DataGridList's row menu on small screens)
const ListActionMenuItems = ({ eventbus }) => {
    const theme = useTheme()
    const { checkPermission } = useAuth()
    const menuitemsx = {
        display: 'flex',
        alignItems: 'center',
        minHeight: 'auto',
        p: '8px 16px',
        '& .anticon': { color: theme.palette.primary.main, mr: '10px' },
    }
    return checkPermission('stock_unit.create') ? (
        <MenuItem
            sx={menuitemsx}
            onClick={(e) => {
                e.stopPropagation()
                eventbus.fire(EVENT_STOCKUNIT_ADD_INIT)
            }}
        >
            <PlusCircleOutlined /> Add Stock Unit
        </MenuItem>
    ) : null
}

/**
 * Active/Inactive status chip, same interaction as CellStatus in
 * Product.List.jsx: click to toggle, optimistic local update.
 */
const CellStatus = ({ item, onStatusChange }) => {
    const [loading, setLoading] = useState(false)
    const [currentStatus, setCurrentStatus] = useState(item.status)

    const handleClick = async () => {
        if (loading) return
        try {
            setLoading(true)
            const response = await stockUnitToggleStatus(item.id)
            const newStatus = response?.data?.status ?? (currentStatus === 'active' ? 'inactive' : 'active')
            setCurrentStatus(newStatus)
            onStatusChange?.({ ...item, status: newStatus })
        } catch (error) {
            console.error('Failed to update stock unit status:', error)
        } finally {
            setLoading(false)
        }
    }

    const isActive = currentStatus === 'active'
    const bgColor = isActive ? '#e2ffc8' : '#f9e3e1'
    const fgColor = isActive ? '#389E0D' : '#CF1322'
    const cellFontSize = { xs: 18, xl: 22 };

    return (
        <Chip
            label={isActive ? 'Active' : 'Inactive'}
            size="medium"
            icon={isActive ? <CheckCircleOutlined sx={{ fontSize: cellFontSize }} style={{ color: fgColor }} /> : <CloseCircleOutlined sx={{ fontSize: cellFontSize }} style={{ color: fgColor }} />}
            onClick={handleClick}
            disabled={loading}
            sx={{
                mt: '5px',
                color: fgColor,
                backgroundColor: bgColor,
                cursor: loading ? 'wait' : 'pointer',
                opacity: loading ? 0.6 : 1,
                fontSize: { xs: 14, xl: 15 },
                '&:hover': { opacity: 0.8, transform: 'scale(1.05)' },
                transition: 'all 0.2s ease',
            }}
        />
    )
}

/**
 * Get props details data for cells.
 */
const getStockUnitPropsData = () => {
    const events = {
        detail_init: EVENT_STOCKUNIT_DETAIL_INIT,
        update_init: EVENT_STOCKUNIT_UPDATE_INIT,
        delete_init: EVENT_STOCKUNIT_DELETE_INIT,
    }

    const actions = {
        detail: {
            tooltip: 'View Details',
            menu: 'View Details',
            icon: <EyeOutlined />,
            event: events.detail_init,
        },
        update: {
            tooltip: 'Edit Stock Unit',
            menu: 'Edit',
            icon: <EditOutlined />,
            event: events.update_init,
        },
        delete: {
            tooltip: 'Delete Stock Unit',
            menu: 'Delete',
            icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
            event: events.delete_init,
        },
    }

    return {
        actions,
        events,
    }
}

const CellActions = ({ item, eventbus }) => {
    const { checkPermission } = useAuth()
    return (
        <Box sx={{ display: 'flex', gap: 1 }}>
            {checkPermission('stock_unit.view') && (
                <EyeOutlined
                    style={{ cursor: 'pointer' }}
                    title="View Details"
                    onClick={() => eventbus.fire(EVENT_STOCKUNIT_DETAIL_INIT, item)}
                />
            )}
            {checkPermission('stock_unit.update') && (
                <EditOutlined
                    style={{ cursor: 'pointer' }}
                    title="Edit"
                    onClick={() => eventbus.fire(EVENT_STOCKUNIT_UPDATE_INIT, item)}
                />
            )}
            {checkPermission('stock_unit.delete') && (
                <DeleteOutlined
                    style={{ cursor: 'pointer', color: '#ff4d4f' }}
                    title="Delete"
                    onClick={() => eventbus.fire(EVENT_STOCKUNIT_DELETE_INIT, item)}
                />
            )}
        </Box>
    )
}

// Mobile column configuration
const mobile_cols = (eventbus) => [
    {
        field: 'name',
        headerName: 'Detail',
        flex: 1,
        renderCell: (props) => (
            <Box sx={{ py: 1 }}>
                <Typography variant="subtitle1">{props.row.name}</Typography>
            </Box>
        ),
    },
]

// Desktop column configuration
const columns = (eventbus) => [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 80, renderCell: (props) => (<CellRenderer type="name" item={props.row} propsDetails={getStockUnitPropsData(props.row)} />) },
    { field: 'status', headerName: 'Status', flex: 0.6, renderCell: (props) => (<CellStatus item={props.row} onStatusChange={() => { }} />), },
    { field: 'action', headerName: 'Action', sortable: false, flex: 0.5, minWidth: 150, maxWidth: 150, renderCell: (props) => (<CellRenderer type="action" item={props.row} propsDetails={getStockUnitPropsData(props.row)} />) }
];

export default StockUnitList